"use client";

import { kebabCase, startCase } from "lodash";
import Image from "next/image";
import { useState } from "react";
import { Customer, IntegrationType } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { INTEGRATION_PROVIDERS } from "@/lib/constants/integrations";
import { cn, getCustomerSource } from "@/lib/utils";

const FALLBACK_ICON = "/images/icon.png";
const PAID_ADS_UTM_MEDIUMS = ["paid", "ppc", "cpc", "cpm", "cpa", "cpc", "cpm", "cpa"];

// Custom display labels for specific sources
const SOURCE_LABELS: Record<string, string> = {
  sms: "Incoming text",
  voice: "Incoming call",
  csv: "Upload",
  api: "Helios API",
  manual: "Manually added",
  form: "Form submission",
  email: "Incoming email",
};

function getSourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? startCase(source);
}

/** Plain-language source for captions (rows, tooltips), matching the badge. */
export function customerSourceLabel(customer: Customer): string {
  const source = getCustomerSource(customer);
  const utmSource = customer?.properties?.values?.utm_source;
  const utmMedium = customer?.properties?.values?.utm_medium;
  if (utmSource) {
    return PAID_ADS_UTM_MEDIUMS.includes(utmMedium) ? "Paid ads" : startCase(utmSource);
  }
  return getSourceLabel(source);
}

export function customerSourceIconType(customer: Customer): string {
  return customer?.properties?.values?.utm_source || getCustomerSource(customer);
}

export function IntegrationImage({
  integrationType,
  className = "",
}: {
  integrationType: IntegrationType | "time" | "logic" | string;
  className?: string;
}) {
  const [hasError, setHasError] = useState(false);
  const integrationProvider = INTEGRATION_PROVIDERS.find(
    (i) => i.id === kebabCase(integrationType),
  );

  // Determine the image source
  let imageSrc: string;
  let altText: string;

  if (integrationProvider) {
    imageSrc = integrationProvider.logo;
    altText = integrationProvider.name;
  } else if (!hasError) {
    // Try standard integrations path structure for unknown sources (e.g., UTM sources like facebook, instagram)
    imageSrc = `/images/integrations/${kebabCase(integrationType)}/icon.png`;
    altText = startCase(integrationType);
  } else {
    imageSrc = FALLBACK_ICON;
    altText = "Helios";
  }

  return (
    <Image
      src={imageSrc}
      width={50}
      height={50}
      alt={altText}
      className={cn("shrink-0 block bg-background dark:bg-transparent rounded-sm", className)}
      onError={() => setHasError(true)}
    />
  );
}

export function CustomerSourceBadge({ customer }: { customer: Customer }) {
  const source = getCustomerSource(customer);
  const utmSource = customer?.properties?.values?.utm_source;
  const utmMedium = customer?.properties?.values?.utm_medium;

  // Apply UTM treatment if utm_source exists (regardless of source)
  if (utmSource) {
    // Use UTM source for the icon, display "Paid ads" if utm_medium is a paid type, otherwise the source name
    const displayText = PAID_ADS_UTM_MEDIUMS.includes(utmMedium)
      ? "Paid ads"
      : startCase(utmSource);
    return (
      <Badge variant="secondary">
        <IntegrationImage
          integrationType={utmSource}
          className="inline-block w-4 h-4 mr-2 rounded-sm"
        />
        <span>{displayText}</span>
      </Badge>
    );
  }

  // Check if there's a known integration provider
  const integration = INTEGRATION_PROVIDERS.find((i) => i.id === kebabCase(source));

  if (!integration) {
    // Use Helios icon for sources without a known integration
    return (
      <Badge variant="secondary">
        <Image
          src="/images/icon.png"
          width={50}
          height={50}
          alt="Helios"
          className="inline-block w-4 h-4 mr-2 bg-background dark:bg-transparent rounded-sm"
        />
        <span>{getSourceLabel(source)}</span>
      </Badge>
    );
  }

  return (
    <Badge variant="secondary">
      <IntegrationImage
        integrationType={source as IntegrationType}
        className="inline-block w-4 h-4 mr-2 rounded-sm"
      />
      <span>{getSourceLabel(source)}</span>
    </Badge>
  );
}
